import { useEffect, useState, useCallback, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { ChatCircleDots, PaperPlaneTilt, Prohibit, Flag, ArrowLeft, Buildings } from "@phosphor-icons/react";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso), s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return "à l'instant";
  if (s < 3600) return `il y a ${Math.floor(s / 60)} min`;
  if (s < 86400) return `il y a ${Math.floor(s / 3600)} h`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

export default function MarketplaceMessages() {
  const { refreshUnread } = useOutletContext() || {};
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [conv, setConv] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadConvs = useCallback(async () => {
    try {
      const { data } = await api.get("/marketplace/conversations");
      setConvs(data.items || []);
    } finally { setLoading(false); }
  }, []);

  const openConv = useCallback(async (id) => {
    setActive(id);
    try {
      const { data } = await api.get(`/marketplace/conversations/${id}`);
      setConv(data.conversation);
      setMessages(data.messages || []);
      setConvs((prev) => prev.map((c) => c.id === id ? { ...c, unread: 0 } : c));
      refreshUnread && refreshUnread();
    } catch { toast.error("Conversation introuvable"); }
  }, [refreshUnread]);

  useEffect(() => { loadConvs(); }, [loadConvs]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || !active) return;
    setSending(true);
    try {
      const { data } = await api.post(`/marketplace/conversations/${active}/messages`, { message: text.trim() });
      setMessages((m) => [...m, data.message]);
      setText("");
      loadConvs();
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); }
    finally { setSending(false); }
  };

  const toggleBlock = async () => {
    if (!conv) return;
    try {
      const { data } = await api.post(`/marketplace/conversations/${active}/block`, { unblock: conv.blocked_by_me });
      setConv((c) => ({ ...c, blocked: data.blocked, blocked_by_me: data.blocked }));
      toast.success(data.blocked ? "Organisation bloquée" : "Blocage retiré");
    } catch (e) { toast.error(e.response?.data?.detail || "Action impossible"); }
  };

  const report = async () => {
    const reason = window.prompt("Motif du signalement (optionnel) :", "");
    if (reason === null) return;
    try {
      await api.post(`/marketplace/conversations/${active}/report`, { reason });
      toast.success("Signalement transmis à la modération");
    } catch { toast.error("Signalement impossible"); }
  };

  return (
    <div data-testid="marketplace-messages">
      <h1 className="font-display text-2xl font-bold mb-1">Messagerie interne</h1>
      <p className="text-muted text-sm mb-6">Échangez en toute confidentialité — aucune coordonnée n'est partagée.</p>

      <div className="grid md:grid-cols-[320px_1fr] gap-4 surface rounded-lg overflow-hidden" style={{ minHeight: 480 }}>
        {/* Liste */}
        <div className={`border-r border-border ${active ? "hidden md:block" : ""}`} data-testid="mk-conv-list">
          {loading ? (
            <div className="p-4 text-muted text-sm animate-pulse">Chargement…</div>
          ) : convs.length === 0 ? (
            <div className="p-6 text-center text-muted text-sm">
              <ChatCircleDots size={32} weight="duotone" className="text-accent mx-auto mb-2" />
              Aucune conversation. Contactez un profil depuis l'onglet Profils.
            </div>
          ) : convs.map((c) => (
            <button key={c.id} onClick={() => openConv(c.id)} data-testid={`mk-conv-${c.id}`}
              className={`w-full text-left px-4 py-3 border-b border-border transition-colors ${active === c.id ? "bg-surface-2" : "hover:bg-surface-2/50"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-fg text-sm truncate flex items-center gap-1.5"><Buildings size={13} className="text-muted" /> {c.counterpart_org_name}</span>
                {c.unread > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-accent text-black text-[11px] font-bold flex items-center justify-center">{c.unread}</span>}
              </div>
              {c.about?.label && <div className="text-[11px] text-accent truncate mt-0.5">{c.about.type === "profile" ? "Profil" : c.about.type === "request" ? "Demande" : ""} · {c.about.label}</div>}
              <div className="text-xs text-muted truncate mt-0.5">{c.last_sender_is_me ? "Vous : " : ""}{c.last_preview}</div>
              <div className="text-[10px] text-muted mt-0.5">{timeAgo(c.last_at)}</div>
            </button>
          ))}
        </div>

        {/* Fil */}
        <div className={`flex flex-col ${!active ? "hidden md:flex" : "flex"}`} style={{ maxHeight: 560 }}>
          {!conv ? (
            <div className="flex-1 flex items-center justify-center text-muted text-sm p-6">Sélectionnez une conversation.</div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border">
                <div className="flex items-center gap-2 min-w-0">
                  <button className="md:hidden text-muted" onClick={() => { setActive(null); setConv(null); }}><ArrowLeft size={18} /></button>
                  <div className="min-w-0">
                    <div className="font-semibold text-fg text-sm truncate flex items-center gap-1.5"><Buildings size={13} className="text-muted" /> {conv.counterpart_org_name}</div>
                    {conv.about?.label && <div className="text-[11px] text-accent truncate">{conv.about.label}</div>}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={report} data-testid="mk-report-btn" title="Signaler" className="p-2 text-muted hover:text-fg rounded-md"><Flag size={16} /></button>
                  <button onClick={toggleBlock} data-testid="mk-block-btn" title={conv.blocked_by_me ? "Débloquer" : "Bloquer"}
                    className={`p-2 rounded-md ${conv.blocked_by_me ? "text-accent" : "text-muted hover:text-red-400"}`}><Prohibit size={16} /></button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="mk-thread">
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.is_me ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-lg px-3 py-2 ${m.is_me ? "bg-accent text-black" : "bg-surface-2 text-fg border border-border"}`}>
                      {!m.is_me && <div className="text-[10px] font-semibold opacity-70 mb-0.5">{m.sender_org_name}</div>}
                      <div className="text-sm whitespace-pre-line">{m.body}</div>
                      <div className={`text-[10px] mt-1 ${m.is_me ? "text-black/60" : "text-muted"}`}>{timeAgo(m.at)}</div>
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>

              {conv.blocked ? (
                <div className="px-4 py-3 border-t border-border text-center text-xs text-muted">
                  {conv.blocked_by_me ? "Vous avez bloqué cette organisation." : "Cette conversation est bloquée."}
                </div>
              ) : (
                <div className="px-4 py-3 border-t border-border flex items-end gap-2">
                  <textarea value={text} onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    data-testid="mk-message-input" placeholder="Écrivez un message…"
                    className="flex-1 bg-surface-2 border border-border rounded-md text-sm px-3 py-2 min-h-[42px] max-h-32 resize-none text-fg" />
                  <button onClick={send} disabled={sending || !text.trim()} data-testid="mk-send-btn"
                    className="bg-accent text-black font-semibold p-2.5 rounded-md hover-lift disabled:opacity-50"><PaperPlaneTilt size={18} weight="fill" /></button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
